/**
 * The KMD editor panel (KHIYARSA-EDITOR-1 Phase 3, §B0 + §B2).
 *
 * A resizable side dock, not a dialog: the owner's decision that rendering is
 * live into the canvas makes a modal impossible — you cannot watch a diagram
 * build behind a sheet that covers it. It docks on the right in LTR and on the
 * left in RTL, and remembers its width.
 *
 * CodeMirror loads lazily behind the same state machine the upstream mermaid
 * input uses (loading → ready → plain-textarea fallback), so the editor stays
 * out of the initial bundle; the KMD compiler is already code-split for the
 * same PWA-precache reason.
 */
import { useApp } from "@excalidraw/excalidraw/components/App";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useAtom } from "../../../app-jotai";
import { useAppLangCode } from "../../../app-language/language-state";
import { renderFix, renderMessage } from "../diagnostics";
import { useKmdDiagnosticLocale } from "../ui/strings";

import {
  DEFAULT_PANEL_WIDTH,
  MAX_PANEL_WIDTH,
  MIN_PANEL_WIDTH,
  kmdEditorAtom,
  readStoredWidth,
  storeWidth,
} from "./atoms";
import {
  adoptProvisional,
  clearDraft,
  discardRecovery,
  loadDraft,
  pendingRecovery,
  saveDraft,
} from "./drafts";
import { KmdButtonRail } from "./KmdButtonRail";
import { KmdStylePanel } from "./KmdStylePanel";
import { applyAction } from "./insert";
import { useEditorT } from "./strings";
import { useLiveRender } from "./useLiveRender";

import "./KmdEditorPanel.scss";

import type { KmdDiagnostic } from "../types";
import type { KmdCodeEditorProps } from "./KmdCodeEditor";
import type { ActionId } from "./insert";

type EditorState = "loading" | "ready" | "fallback";

const SPINNER_DELAY_MS = 300;
const DRAFT_DEBOUNCE_MS = 700;

const STARTER = `---
khiyarsa: 1
type: flow
---

## First stage
- Step one
- Step two
`;

/** the scene identity a draft is filed under (Q5: per scene, never global) */
const useSceneId = (): string | null => {
  try {
    const room = new URLSearchParams(window.location.hash.slice(1)).get("room");
    if (room) {
      return `room:${room.split(",")[0]}`;
    }
    const json = new URLSearchParams(window.location.hash.slice(1)).get("json");
    if (json) {
      return `json:${json.split(",")[0]}`;
    }
  } catch {
    /* fall through */
  }
  return null;
};

export const KmdEditorPanel = () => {
  const app = useApp();
  const t = useEditorT();
  const diagLocale = useKmdDiagnosticLocale();
  const [langCode] = useAppLangCode();
  const rtl = langCode.startsWith("ar");
  const sceneId = useSceneId();

  const [dialog, setDialog] = useAtom(kmdEditorAtom);
  const [text, setText] = useState("");
  const [caret, setCaret] = useState(0);
  /** selection the editor should adopt after a button insert */
  const [pendingSelection, setPendingSelection] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [width, setWidth] = useState(DEFAULT_PANEL_WIDTH);
  const [editorState, setEditorState] = useState<EditorState>("loading");
  const [recovery, setRecovery] = useState<string | null>(null);
  const [tab, setTab] = useState<"write" | "style">("write");
  const EditorRef = useRef<React.ComponentType<KmdCodeEditorProps> | null>(
    null,
  );
  const resizing = useRef(false);

  const { result, gate, bindState, pending, rebind, commit, discard, fit } =
    useLiveRender({ text, active: dialog.open });

  // ---- width -------------------------------------------------------------
  useEffect(() => setWidth(readStoredWidth()), []);

  const onResizeStart = useCallback(
    (event: React.PointerEvent) => {
      event.preventDefault();
      resizing.current = true;
      const startX = event.clientX;
      const startWidth = width;
      const move = (e: PointerEvent) => {
        if (!resizing.current) {
          return;
        }
        // dragging the handle inward widens the panel on both sides; in RTL
        // the panel is on the left, so the sign flips
        const delta = rtl ? e.clientX - startX : startX - e.clientX;
        const next = Math.min(
          MAX_PANEL_WIDTH,
          Math.max(MIN_PANEL_WIDTH, startWidth + delta),
        );
        setWidth(next);
      };
      const up = () => {
        resizing.current = false;
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        setWidth((w) => {
          storeWidth(w);
          return w;
        });
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    },
    [rtl, width],
  );

  // ---- lazy editor -------------------------------------------------------
  useEffect(() => {
    if (!dialog.open || EditorRef.current) {
      return;
    }
    let cancelled = false;
    const spinner = window.setTimeout(() => {
      if (!cancelled) {
        setEditorState("loading");
      }
    }, SPINNER_DELAY_MS);
    import("./KmdCodeEditor")
      .then((mod) => {
        if (cancelled) {
          return;
        }
        EditorRef.current = mod.default;
        setEditorState("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setEditorState("fallback");
        }
      })
      .finally(() => window.clearTimeout(spinner));
    return () => {
      cancelled = true;
      window.clearTimeout(spinner);
    };
  }, [dialog.open]);

  // ---- seed + draft restore ---------------------------------------------
  useEffect(() => {
    if (!dialog.open) {
      return;
    }
    if (dialog.initialText !== undefined) {
      setText(dialog.initialText);
      return;
    }
    if (sceneId) {
      adoptProvisional(window.localStorage, sceneId);
    }
    const draft = loadDraft(window.localStorage, sceneId);
    if (draft) {
      setText(draft.text);
      setCaret(draft.caret);
      return;
    }
    const stray = pendingRecovery(window.localStorage);
    if (stray && !sceneId) {
      // never restore silently into a scene that has no identity — offer it
      setRecovery(stray.text);
      setText((current) => current || STARTER);
      return;
    }
    setText((current) => current || STARTER);
  }, [dialog.open, dialog.initialText, sceneId]);

  // ---- draft autosave ----------------------------------------------------
  useEffect(() => {
    if (!dialog.open) {
      return;
    }
    const timer = window.setTimeout(() => {
      saveDraft(
        window.localStorage,
        sceneId,
        { text, caret, bound: bindState === "bound" },
        Date.now(),
      );
    }, DRAFT_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [text, caret, bindState, sceneId, dialog.open]);

  const describe = useCallback(
    (d: KmdDiagnostic) => renderMessage(d, diagLocale),
    [diagLocale],
  );

  const diagnostics = useMemo(
    () => (result?.diagnostics ?? []).filter((d) => d.code !== "KMD080"),
    [result],
  );

  const onAction = useCallback(
    (action: ActionId) => {
      const family = result?.doc?.family ?? null;
      if (!family) {
        return;
      }
      const next = applyAction(text, caret, action, family);
      setText(next.text);
      setPendingSelection({
        start: next.selectionStart,
        end: next.selectionEnd,
      });
    },
    [text, caret, result],
  );

  const onNewDiagram = useCallback((starter: string) => {
    setText(starter);
    setPendingSelection(null);
  }, []);

  const close = useCallback(() => {
    commit();
    setDialog({ open: false });
  }, [commit, setDialog]);

  if (!dialog.open) {
    return null;
  }

  const Editor = EditorRef.current;
  const counts = {
    error: diagnostics.filter((d) => d.severity === "error").length,
    warning: diagnostics.filter((d) => d.severity === "warning").length,
  };

  return (
    <aside
      className={`kmd-editor kmd-editor--${rtl ? "start" : "end"}`}
      style={{ width }}
      dir={rtl ? "rtl" : "ltr"}
      aria-label={t("panelTitle")}
      // Tells the viewport this surface occludes the canvas, so "fit" (and
      // anything else using `offsets: { ui: true }`) keeps the diagram clear
      // of the panel. The side is resolved geometrically from where the node
      // actually renders, so RTL docking is handled without telling it.
      data-viewport-ui="side"
      // surfaced for end-to-end checks; the footer shows the same thing
      data-kmd-state={bindState}
    >
      <div
        className="kmd-editor__resize"
        onPointerDown={onResizeStart}
        role="separator"
        aria-orientation="vertical"
      />

      <header className="kmd-editor__header">
        <span className="kmd-editor__title">{t("panelTitle")}</span>
        {result?.doc?.family && (
          <span className="kmd-editor__family">{result.doc.family}</span>
        )}
        <span className="kmd-editor__spacer" />
        <div className="kmd-editor__tabs">
          {(["write", "style"] as const).map((name) => (
            <button
              key={name}
              className={`kmd-editor__tab${tab === name ? " is-active" : ""}`}
              onClick={() => setTab(name)}
            >
              {t(name === "write" ? "tabWrite" : "tabStyle")}
            </button>
          ))}
        </div>
        <button
          className="kmd-editor__icon"
          onClick={fit}
          title={t("fitDiagram")}
        >
          ⤢
        </button>
        <button className="kmd-editor__icon" onClick={close} title={t("close")}>
          ✕
        </button>
      </header>

      {recovery !== null && (
        <div className="kmd-editor__banner kmd-editor__banner--info">
          <span>{t("recoverPrompt")}</span>
          <div className="kmd-editor__banner-actions">
            <button
              onClick={() => {
                setText(recovery);
                discardRecovery(window.localStorage);
                setRecovery(null);
              }}
            >
              {t("recoverRestore")}
            </button>
            <button
              onClick={() => {
                discardRecovery(window.localStorage);
                setRecovery(null);
              }}
            >
              {t("recoverDiscard")}
            </button>
          </div>
        </div>
      )}

      {gate !== "production" && (
        <div
          className={`kmd-editor__banner kmd-editor__banner--${
            gate === "blocked" ? "warn" : "info"
          }`}
        >
          <span>
            {t(gate === "blocked" ? "gateBlocked" : "gateExperimental")}
          </span>
        </div>
      )}

      {bindState === "detached" && (
        <div className="kmd-editor__banner kmd-editor__banner--warn">
          <span>{t("detached")}</span>
          <div className="kmd-editor__banner-actions">
            <button onClick={rebind}>{t("rerenderFromText")}</button>
          </div>
        </div>
      )}

      {tab === "write" && (
        <KmdButtonRail
          family={result?.doc?.family ?? null}
          onAction={onAction}
          onNewDiagram={onNewDiagram}
        />
      )}

      {tab === "style" && (
        <div className="kmd-editor__body kmd-editor__body--scroll">
          {result?.doc?.family ? (
            <KmdStylePanel
              text={text}
              caret={caret}
              family={result.doc.family}
              onChange={setText}
            />
          ) : (
            <p className="kmd-style__reason">{t("why_noTarget")}</p>
          )}
        </div>
      )}

      {/* the editor stays mounted while the style tab is open so the caret,
          undo history and scroll position survive tab switching */}
      <div
        className="kmd-editor__body"
        style={tab === "style" ? { display: "none" } : undefined}
      >
        {editorState === "ready" && Editor ? (
          <Editor
            value={text}
            onChange={setText}
            onCaret={setCaret}
            selection={pendingSelection}
            onSelectionApplied={() => setPendingSelection(null)}
            diagnostics={diagnostics}
            describe={describe}
            theme={app.state.theme}
            rtl={rtl}
            initialCaret={caret}
            placeholder={t("placeholder")}
          />
        ) : (
          <textarea
            className="kmd-editor__fallback"
            dir="ltr"
            spellCheck={false}
            value={text}
            placeholder={t("placeholder")}
            onChange={(e) => setText(e.target.value)}
          />
        )}
      </div>

      {diagnostics.length > 0 && (
        <div className="kmd-editor__problems">
          <ul>
            {diagnostics.slice(0, 20).map((d, i) => (
              <li key={i} className={`kmd-editor__problem--${d.severity}`}>
                <code>{d.code}</code>
                {d.span ? ` · ${t("line")} ${d.span.line}` : ""} —{" "}
                {renderMessage(d, diagLocale)}
                {renderFix(d, diagLocale) ? (
                  <em> → {renderFix(d, diagLocale)}</em>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      <footer className="kmd-editor__footer">
        <span
          className={`kmd-editor__state kmd-editor__state--${bindState}`}
          title={t(`state_${bindState}` as never)}
        >
          {t(`state_${bindState}` as never)}
        </span>
        {pending && <span className="kmd-editor__pending">…</span>}
        <span className="kmd-editor__spacer" />
        {counts.error > 0 && (
          <span className="kmd-editor__count kmd-editor__count--error">
            {counts.error}
          </span>
        )}
        {counts.warning > 0 && (
          <span className="kmd-editor__count kmd-editor__count--warning">
            {counts.warning}
          </span>
        )}
        <button
          className="kmd-editor__btn"
          onClick={() => {
            discard();
            clearDraft(window.localStorage, sceneId);
            setText("");
          }}
        >
          {t("clear")}
        </button>
        <button
          className="kmd-editor__btn kmd-editor__btn--primary"
          onClick={close}
        >
          {t("done")}
        </button>
      </footer>
    </aside>
  );
};
